import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightning, Trophy, ArrowRight, Timer, Fire } from "@phosphor-icons/react";

const SLIDES = [
  {
    img: "https://static.prod-images.emergentagent.com/jobs/77c7f82e-9496-46da-8450-a0e1e07f0b18/images/b151dac9fc77bcb801630d6822f5816ab8eed45db80bcf44760a7fb6ee8d9c8a.png",
    badge: "DESAFIO SEMANAL",
    title: "iPhone 17 ou Moto Elétrica",
    subtitle: "1° lugar no ranking semanal escolhe o prêmio!",
    cta: "Começar Agora",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/77c7f82e-9496-46da-8450-a0e1e07f0b18/images/be83b6313def5331cacb1e1ad2726cc8701932fb62959aab611d49e1364837aa.png",
    badge: "PRÊMIOS REAIS",
    title: "R$1.000 + R$200 em Cash",
    subtitle: "2° e 3° lugar também ganham! Não fique de fora.",
    cta: "Ver Tarefas",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/77c7f82e-9496-46da-8450-a0e1e07f0b18/images/11d55c68abed95fa41f0d8f54451b2b8607906b04822be2f5fa57f95331f270e.png",
    badge: "GANHE PONTOS",
    title: "Complete Tarefas Diárias",
    subtitle: "Visite o clube, indique amigos, poste fotos e suba no ranking!",
    cta: "Fazer Tarefas",
  },
];

// Countdown to next Sunday 23:59
const getTimeLeft = () => {
  const now = new Date();
  const daysUntilSunday = (7 - now.getDay()) % 7 || 7;
  const nextSunday = new Date(now);
  nextSunday.setDate(now.getDate() + daysUntilSunday);
  nextSunday.setHours(23, 59, 59, 0);
  const diff = nextSunday - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

// Carousel for Dashboard home
export const PromoCarousel = ({ onClickAction }) => {
  const [current, setCurrent] = useState(0);
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrent((c) => (c + 1) % SLIDES.length), 4000);
    return () => clearInterval(interval);
  }, []);

  const slide = SLIDES[current];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClickAction}
      className="relative rounded-2xl overflow-hidden cursor-pointer group mb-4"
      style={{ height: "180px" }}
      data-testid="promo-carousel"
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={current}
          src={slide.img}
          alt={slide.title}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
      <div className="absolute inset-0 p-5 flex flex-col justify-between">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#FF5A26] text-white text-[10px] font-bold px-2.5 py-1 rounded-full mb-2">
            <Lightning size={12} weight="fill" />
            {slide.badge}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={current} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <h3 className="text-white text-xl font-black leading-tight">{slide.title}</h3>
              <p className="text-white/70 text-xs mt-1">{slide.subtitle}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-1.5 bg-black/40 rounded-full px-3 py-1.5">
            <Timer size={14} weight="fill" className="text-yellow-400" />
            <span className="text-white text-[11px] font-bold font-mono">
              {timeLeft.d}d {String(timeLeft.h).padStart(2, "0")}:{String(timeLeft.m).padStart(2, "0")}:{String(timeLeft.s).padStart(2, "0")}
            </span>
          </div>
          <div className="bg-[#FF5A26] text-white text-xs font-bold px-4 py-2 rounded-full group-hover:bg-[#E04818] transition-colors flex items-center gap-1.5">
            {slide.cta} <ArrowRight size={14} weight="bold" />
          </div>
        </div>
      </div>
      {/* Dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
        {SLIDES.map((_, i) => (
          <button key={i} onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
            className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-white w-5" : "bg-white/40"}`} />
        ))}
      </div>
    </motion.div>
  );
};

// Fullscreen promo overlay for Ranking page
export const PromoFullscreen = ({ onClose, onGoToChallenges }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-[#042F2E] flex flex-col"
      data-testid="promo-fullscreen"
    >
      {/* Close */}
      <button onClick={onClose}
        className="absolute top-4 right-4 z-10 text-white/60 hover:text-white text-sm font-bold bg-white/10 rounded-full px-4 py-2"
        data-testid="promo-fs-close">
        Fechar
      </button>

      {/* Image */}
      <div className="relative flex-shrink-0" style={{ height: "45%" }}>
        <img
          src="https://static.prod-images.emergentagent.com/jobs/77c7f82e-9496-46da-8450-a0e1e07f0b18/images/b151dac9fc77bcb801630d6822f5816ab8eed45db80bcf44760a7fb6ee8d9c8a.png"
          alt="Prêmios" className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#042F2E] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="flex-1 px-6 -mt-10 relative flex flex-col justify-between pb-8">
        <div>
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
            <div className="inline-flex items-center gap-2 bg-[#FF5A26] text-white text-xs font-bold px-4 py-2 rounded-full mb-4">
              <Fire size={16} weight="fill" />
              CAMPANHA SEMANAL
            </div>
            <h1 className="text-white text-3xl font-black leading-tight mb-3">
              Ganhe um iPhone 17<br />ou Moto Elétrica!
            </h1>
          </motion.div>

          {/* Prizes */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.35 }}
            className="space-y-2.5 mb-6">
            {[
              { pos: "1°", prize: "iPhone 17 Pro Max ou Moto Elétrica", color: "text-yellow-400" },
              { pos: "2°", prize: "R$ 1.000,00", color: "text-gray-300" },
              { pos: "3°", prize: "R$ 200,00", color: "text-orange-400" },
            ].map((p) => (
              <div key={p.pos} className="flex items-center gap-3">
                <span className={`font-black text-lg ${p.color}`}>{p.pos}</span>
                <span className="text-white font-semibold text-sm">{p.prize}</span>
              </div>
            ))}
          </motion.div>

          {/* Countdown */}
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
            className="bg-white/10 rounded-2xl p-4 flex items-center justify-center gap-4">
            <Timer size={22} weight="fill" className="text-yellow-400" />
            <div className="flex gap-3 font-mono">
              {[
                { val: timeLeft.d, label: "DIAS" },
                { val: timeLeft.h, label: "HRS" },
                { val: timeLeft.m, label: "MIN" },
                { val: timeLeft.s, label: "SEG" },
              ].map((t) => (
                <div key={t.label} className="text-center">
                  <span className="text-white text-2xl font-black block">{String(t.val).padStart(2, "0")}</span>
                  <span className="text-white/40 text-[9px] font-bold">{t.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.65 }}>
          <button
            onClick={onGoToChallenges}
            className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-5 text-lg shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-transform"
            data-testid="promo-fs-go"
          >
            <Trophy size={22} weight="fill" />
            Ir para Tarefas Diárias
            <ArrowRight size={20} weight="bold" />
          </button>
          <p className="text-white/40 text-xs text-center mt-3">Complete tarefas para acumular pontos e subir no ranking</p>
        </motion.div>
      </div>
    </motion.div>
  );
};
